import { useEffect, useState } from "react";
import { voteTimeRemaining, type CommandState } from "@/session/command.ts";
import type { SessionApi } from "@/session/useSession.ts";

interface CommandBarProps {
  readonly session: SessionApi;
  readonly myId: string;
  /** Known locally even before the roster has synced our own record back. */
  readonly myCallsign: string;
}

/**
 * The command chair.
 *
 * Three states, and each shows exactly one obvious action:
 *
 *   vacant     -> "Take command". One tap, no vote. Most sessions live here.
 *   held       -> who has it, and a quiet way to challenge.
 *   contested  -> the ballot, front and centre, with a countdown.
 *
 * The challenge button is deliberately understated. Contesting command mid-
 * match is disruptive, and a button that looks eager will get pressed for fun.
 */
export function CommandBar({ session, myId, myCallsign }: CommandBarProps): React.ReactElement {
  const { commandState, sync } = session;
  // A brand-new session and one that has simply not finished pulling in what
  // a teammate already set up look identical for a moment — both show
  // nothing. Only the sync status tells them apart, so trust it before
  // reporting the chair empty.
  const settling = sync.status !== "connected" && sync.status !== "error";

  if (commandState.kind === "contested") {
    return <ContestedBar session={session} state={commandState} myId={myId} />;
  }

  if (commandState.kind === "vacant") {
    return (
      <div className="flex items-center gap-3 border-b border-border-subtle bg-surface-raised px-3 py-1.5">
        {settling ? (
          <span className="text-xs text-text-muted">Connecting…</span>
        ) : (
          <>
            <span className="text-xs text-text-muted">No commander</span>
            <button
              type="button"
              onClick={session.claimCommand}
              className="rounded bg-accent px-3 py-1 text-xs font-semibold text-surface"
            >
              Take command
            </button>
          </>
        )}
      </div>
    );
  }

  const commander = session.players.find((player) => player.id === commandState.commanderId);
  const isMine = commandState.commanderId === myId;
  // If your own roster record has not synced back yet, you are still not
  // "unknown" to yourself — fall back to the identity you joined with rather
  // than a placeholder that reads like something has gone wrong.
  const commanderName = commander?.callsign ?? (isMine ? myCallsign : "unknown");

  return (
    <div className="flex items-center gap-3 border-b border-border-subtle bg-surface-raised px-3 py-1.5">
      <span className="text-xs text-text-muted">Commander</span>
      <span className="text-xs font-semibold text-accent">
        {commanderName}
        {isMine && <span className="ml-1.5 text-text-muted">(you)</span>}
      </span>

      <div className="ml-auto">
        {isMine ? (
          <button
            type="button"
            onClick={session.resignCommand}
            className="rounded px-2 py-1 text-xs text-text-muted transition-colors hover:text-text-secondary"
          >
            Stand down
          </button>
        ) : (
          <button
            type="button"
            onClick={session.contestCommand}
            title="Calls a vote. Use this when the team actually needs a change."
            className="rounded px-2 py-1 text-xs text-text-muted transition-colors hover:text-warning"
          >
            Contest
          </button>
        )}
      </div>
    </div>
  );
}

function ContestedBar({
  session,
  state,
  myId,
}: {
  readonly session: SessionApi;
  readonly state: Extract<CommandState, { kind: "contested" }>;
  readonly myId: string;
}): React.ReactElement {
  const remaining = useCountdown(state.closesAt);

  const challenger = session.players.find((player) => player.id === state.challengerId);
  const incumbent = session.players.find((player) => player.id === state.commanderId);
  const { tally, myVote } = session;

  return (
    <div className="border-b border-warning/40 bg-warning/10 px-3 py-1.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-xs font-semibold text-warning">VOTE</span>

        <span className="text-xs text-text-secondary">
          <strong className="text-text-primary">{challenger?.callsign ?? "Someone"}</strong> to
          replace{" "}
          <strong className="text-text-primary">{incumbent?.callsign ?? "nobody"}</strong>
        </span>

        <span className="numeric text-xs text-text-muted">
          {tally.inFavour} for · {tally.against} against · {tally.needed} more needed
        </span>

        <span className="numeric ml-auto text-xs text-warning">{remaining}s</span>

        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => session.vote(true)}
            aria-pressed={myVote?.inFavour === true}
            className={
              myVote?.inFavour === true
                ? "rounded bg-success px-3 py-1 text-xs font-semibold text-surface"
                : "rounded border border-border-strong px-3 py-1 text-xs text-text-secondary hover:text-text-primary"
            }
          >
            For
          </button>
          <button
            type="button"
            onClick={() => session.vote(false)}
            aria-pressed={myVote?.inFavour === false}
            className={
              myVote?.inFavour === false
                ? "rounded bg-danger px-3 py-1 text-xs font-semibold text-surface"
                : "rounded border border-border-strong px-3 py-1 text-xs text-text-secondary hover:text-text-primary"
            }
          >
            Against
          </button>
          {myVote && (
            <button
              type="button"
              onClick={session.clearVote}
              className="rounded px-2 py-1 text-xs text-text-muted hover:text-text-secondary"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {state.challengerId === myId && (
        <p className="mt-1 text-[11px] text-text-muted">
          You called this vote, so your vote in favour is already counted.
        </p>
      )}
    </div>
  );
}

/** Seconds left on the ballot, ticking once a second. */
function useCountdown(closesAt: number): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, []);

  const remaining = voteTimeRemaining(
    { kind: "contested", commanderId: null, challengerId: "", openedAt: 0, closesAt },
    now,
  );
  return Math.ceil((remaining ?? 0) / 1000);
}
